import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { useParams, Link } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import { useEffect, useState } from 'react';

const categoryNames: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  jilupian: '纪录片',
  dongman: '动漫',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);
  const related = videos.filter((v) => v.category === video?.category && v.id !== id).slice(0, 6);

  useEffect(() => {
    if (video) {
      document.title = `${video.title} - 九九免费视频`;
    }
  }, [video]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-foreground-300 text-6xl"></i>
            <p className="mt-4 text-foreground-500 text-lg">视频未找到</p>
            <Link to="/" className="mt-4 inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const handleRelatedClick = (relatedId: string) => {
    window.REACT_APP_NAVIGATE(`/article/${relatedId}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="relative w-full h-[300px] md:h-[450px] overflow-hidden">
          <img
            src={video.image}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30"></div>
        </div>

        <section className="relative -mt-20 z-10 px-4 md:px-6">
          <div className="max-w-[1920px] mx-auto">
            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${video.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {categoryNames[video.category] || video.category}
                </Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-800 font-medium line-clamp-1">{video.title}</span>
              </nav>

              <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong className="text-foreground-800 font-label">{video.rating}</strong> 评分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {video.duration}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {video.year}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-refresh-line"></i>
                  {video.updateTime} 更新
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {video.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8">
                <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">视频简介</h3>
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">{video.description}</p>
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-[1920px] mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
                {related.map((rVideo) => (
                  <div
                    key={rVideo.id}
                    className="group cursor-pointer"
                    onClick={() => handleRelatedClick(rVideo.id)}
                    data-product-shop=""
                  >
                    <div className="relative aspect-video overflow-hidden rounded-xl mb-3">
                      <img
                        src={rVideo.image}
                        alt={rVideo.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute top-2 right-2">
                        <span className="px-1.5 py-0.5 bg-black/70 backdrop-blur-sm text-white text-[10px] font-label rounded-md">
                          {rVideo.duration}
                        </span>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className="px-1.5 py-0.5 bg-accent-500 text-background-50 text-[10px] font-bold font-label rounded-md flex items-center gap-1">
                          <i className="ri-star-fill text-[8px]"></i>
                          {rVideo.rating}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-heading font-bold text-xs text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {rVideo.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}